import { isEmpty } from "lodash";
import { useMemo, useRef, useState } from "react";
import { useSearchProviderProcedureMutation } from "store/queries/providerProcedure";
import { IProviderProcedure } from "types/ProviderProcedure";

import { useQuery } from "./useQuery";

export const CLEAR_HEALTH_PAYER_NAME = "Clear Health";

/* Re-enable with DefaultServiceDetails / GFEServiceDetails CPT helper + disabled state if we again block search until Visit is valid:
export const CPT_SEARCH_REQUIRES_VISIT_DATE_MESSAGE =
  "Enter visit date before searching";
*/

export function useProviderProcedure({
  payerNameField,
  dateOfService,
}: {
  payerNameField?: string;
  /** YYYY-MM-DD; omit when unknown — backend defaults (e.g. today) */
  dateOfService?: string;
}) {
  const query = useQuery();
  const accountId = query.get("accountId") || "";
  const [filteredProviderProceduresData, setFilteredProviderProceduresData] =
    useState<IProviderProcedure[]>();

  const [searchMutation, { isLoading }] = useSearchProviderProcedureMutation();

  // Track the latest request to prevent race conditions
  const latestRequestIdRef = useRef(0);

  const fetchFilteredProviderProceduresData = async (
    searchInput: string
  ): Promise<IProviderProcedure[] | undefined> => {
    // Increment and capture the request ID for this specific request
    latestRequestIdRef.current += 1;
    const requestId = latestRequestIdRef.current;

    try {
      if (isEmpty(payerNameField)) return [];
      /* Require visit date before calling search (uncomment to restore strict gate):
      if (!dateOfService) {
        if (requestId === latestRequestIdRef.current) {
          setFilteredProviderProceduresData([]);
        }
        return [];
      }
      */
      const response = await searchMutation({
        accountId,
        cptCode: searchInput,
        payerName: payerNameField,
        ...(dateOfService ? { dateOfService } : {}),
      });

      const result = (response as any).data.data.data as IProviderProcedure[];

      // Only update state if this is still the latest request
      if (requestId === latestRequestIdRef.current) {
        setFilteredProviderProceduresData(result);
      }

      return result;
    } catch (error) {
      console.error("An error occurred when filtering:", error);
      return [];
    }
  };

  const procedures = useMemo(() => {
    return (
      filteredProviderProceduresData?.map((providerProcedure) => {
        return providerProcedure.procedure || [];
      }) || []
    );
  }, [filteredProviderProceduresData]);

  const search = async (event: { query: string }) => {
    if (event.query.trim().length) {
      const result = await fetchFilteredProviderProceduresData(
        event.query.toLowerCase()
      );
      return result;
    }
    return [];
  };

  return {
    loadedProviderProcedure: !isLoading,
    procedures,
    providerProcedures: filteredProviderProceduresData || [],
    search,
  };
}
